/**
 * Custom Hook: useFacilityFeatures
 * Provides facility-specific feature flags and utilities
 */

import { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import FacilityService from '../services/api/FacilityService';

export const useFacilityFeatures = () => {
  const { state } = useAppContext();
  const { selectedFacility } = state;
  const [facilityData, setFacilityData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load facility data from service
  useEffect(() => {
    const loadFacility = async () => {
      if (!selectedFacility) {
        setLoading(false);
        return;
      }

      try {
        const result = await FacilityService.getById(selectedFacility);
        if (result.success) {
          setFacilityData(result.data);
        } else {
          // Default to aged care facility
          setFacilityData({
            id: selectedFacility,
            primaryType: 'agedcare',
            facilityTypes: ['agedcare'],
            systemContext: 'agedcare',
            features: {},
            agedCare: { enabled: true }
          });
        }
      } catch (error) {
        console.error('Error loading facility:', error);
        // Default to aged care facility
        setFacilityData({
          id: selectedFacility,
          primaryType: 'agedcare',
          facilityTypes: ['agedcare'],
          systemContext: 'agedcare',
          features: {},
          agedCare: { enabled: true }
        });
      } finally {
        setLoading(false);
      }
    };

    loadFacility();
  }, [selectedFacility]);

  // Determine facility type from actual data
  const facilityTypes = facilityData?.facilityTypes || [];
  const primaryType = facilityData?.primaryType || 'agedcare';
  const agedCareEnabled = facilityData?.agedCare?.enabled === true;

  const isAgedCare = primaryType === 'agedcare' || 
                     facilityTypes.includes('agedcare') ||
                     agedCareEnabled;

  const systemContext = 'agedcare';

  const features = facilityData?.features || {};

  return {
    // Facility info
    facilityId: selectedFacility,
    facilityData,
    facilityConfig: facilityData ? {
      id: facilityData.id,
      name: facilityData.name,
      type: primaryType,
      types: facilityTypes,
      systemContext,
      features
    } : {
      id: selectedFacility,
      name: 'Aged Care Facility',
      type: 'agedcare',
      types: ['agedcare'],
      systemContext: 'agedcare',
      features: {}
    },
    systemContext,
    loading,
    
    // Type checks
    isAgedCare,
    
    // Feature flags
    supportsANACC: features.anacc || false,
    supportsENRMC: features.enrmc || false,
    
    // Helper function
    supportsFeature: (feature) => features[feature] || false
  };
};

export default useFacilityFeatures;
